"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { COCHRANS_Q_TEST_CALCULATOR_STRINGS } from "@/components/tools/cochrans-q-test-calculator";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import { t } from "@/lib/i18n/interpolate";
import {
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import type { CochranQResult } from "@/lib/tool-kit/stats-tests-more/cochran-q";

/** Results for Cochran's Q test calculator. */

type Strings = typeof COCHRANS_Q_TEST_CALCULATOR_STRINGS;

const MATRIX_LIMIT = 40;

export function CochransQResults({
  r,
  rows,
  names,
  alpha,
  strings,
  fmt,
}: {
  r: CochranQResult;
  rows: readonly (readonly number[])[];
  names: readonly string[];
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const reject = r.p < alpha;
  const apa = richTemplate(strings.apaTemplate, {
    df: fmt(r.df),
    n: fmt(r.n),
    q: fixed(r.q, 2, fmt),
    p: apaP(r.p, fmt),
  });
  const sumColumnSquares = r.columnTotals.reduce((acc, c) => acc + c * c, 0);
  const sumRowSquares = r.rowTotals.reduce((acc, x) => acc + x * x, 0);
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.qStatistic} value={n(r.q)} />
        <StatCard label={strings.df} value={fmt(r.df)} />
        <StatCard emphasis label={strings.pValue} value={formatP(r.p, fmt)} />
        <StatCard label={strings.kendallW} value={n(r.kendallW)} />
        <StatCard label={strings.subjects} value={fmt(r.n)} />
        <StatCard label={strings.informative} value={fmt(r.informative)} />
      </StatGrid>
      <Decision reject={reject}>
        {t(reject ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(r.p, fmt),
        })}
      </Decision>
      {r.approximationOk ? null : <p className={HINT}>{strings.smallSample}</p>}
      <p className={HINT}>{strings.informativeNote}</p>
      <h3 className={WIDGET_H3}>{strings.summaryHeading}</h3>
      <ResultTable
        head={[
          strings.condition,
          strings.successes,
          strings.total,
          strings.proportion,
        ]}
        rows={names.map((name, j) => ({
          key: `${j}-${name}`,
          cells: [name, fmt(r.columnTotals[j]), fmt(r.n), n(r.proportions[j])],
        }))}
      />
      <h3 className={WIDGET_H3}>{strings.posthocHeading}</h3>
      <p className={HINT}>
        {t(strings.posthocHint, { m: fmt(r.pairs.length) })}
      </p>
      <ResultTable
        head={[
          strings.comparison,
          strings.b,
          strings.c,
          strings.pExact,
          strings.pChi,
          strings.pBonferroni,
          strings.pHolm,
        ]}
        rows={r.pairs.map((pair) => ({
          key: `${pair.first}-${pair.second}`,
          cells: [
            t(strings.versus, { a: names[pair.first], b: names[pair.second] }),
            fmt(pair.b),
            fmt(pair.c),
            formatP(pair.pExact, fmt),
            formatP(pair.pChiSquare, fmt),
            formatP(pair.pBonferroni, fmt),
            formatP(pair.pHolm, fmt),
          ],
        }))}
      />
      <h3 className={WIDGET_H3}>{strings.matrixHeading}</h3>
      <p className={HINT}>
        {r.n > MATRIX_LIMIT ? strings.matrixHidden : strings.matrixHint}
      </p>
      {r.n > MATRIX_LIMIT ? null : (
        <ResultTable
          head={[strings.subject, ...names, strings.rowTotal]}
          rows={[
            ...rows.map((row, i) => ({
              key: `subject-${i + 1}`,
              cells: [
                fmt(i + 1),
                ...row.map((v) => fmt(v)),
                fmt(r.rowTotals[i]),
              ],
            })),
            {
              key: "totals",
              cells: [
                strings.columnTotals,
                ...r.columnTotals.map((c) => fmt(c)),
                fmt(r.grandTotal),
              ],
            },
          ]}
        />
      )}
      <Working heading={strings.howCalculated}>
        <p>
          {t(strings.workingTotals, {
            t: fmt(r.grandTotal),
            sc: fmt(sumColumnSquares),
            sr: fmt(sumRowSquares),
            k: fmt(r.k),
          })}
        </p>
        <p>
          {t(strings.workingQ, {
            kk: fmt(r.k - 1),
            k: fmt(r.k),
            sc: fmt(sumColumnSquares),
            t: fmt(r.grandTotal),
            sr: fmt(sumRowSquares),
            q: n(r.q),
          })}
        </p>
        <p>
          {t(strings.workingP, {
            df: fmt(r.df),
            q: n(r.q),
            p: formatP(r.p, fmt),
          })}
        </p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={strings.apaNote}
        parts={apa}
      />
    </section>
  );
}
